import { useEffect, useRef, useState } from "react";

import invariant from "tiny-invariant";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine"; // NEW
import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"; // NEW

import { useDispatch } from "react-redux";

import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import useCustomSnackbar from "./useCustomSnackbar";
import { SetDraggingId, SetDraggingIds } from "../../Redux/Reducers/redux-slice-layout";
import { assetService } from "../../Core/Api/services";
import { ObjectType } from "../../Core/Api/Types/object-type";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
import { AddChildrenToTreeObj, MoveChildrenInTreeObj, TreeContext } from "../../Redux/Reducers/redux-slice-tree";
import { RemoveObjs } from "../../Redux/Reducers/redux-slice-drive";

const useDraggable = (
  type: ObjectType | undefined,
  id: number,
  withPortal: boolean,
  dropType: string,
  usage: "drag" | "drop" | "both" | "none",
  selectedItemsIds?: number[],
  context: TreeContext = TreeContext.DriveTreeview
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const ref = useRef<any | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [state, setState] = useState<any>({ type: "idle" });

  const idsRef = useRef<number[]>(selectedItemsIds ?? []);

  useEffect(() => {
    idsRef.current = selectedItemsIds ?? [];
  }, [selectedItemsIds]);

  useEffect(() => {
    if (usage === "none") return;

    const cardEl = ref.current;

    invariant(cardEl);

    return combine(
      usage !== "drop"
        ? draggable({
            element: cardEl,
            getInitialData: () => ({ type: dropType, cardId: id }),
            onDragStart: () => {
              dispatch(SetDraggingId(id));
              dispatch(SetDraggingIds(idsRef.current ? [...idsRef.current.filter((x) => x !== id), id] : [id]));
              setIsDragging(true);
            },
            onDrop: () => {
              dispatch(SetDraggingId(undefined));
              dispatch(SetDraggingIds([]));
              setIsDragging(false);
            },
            onGenerateDragPreview({ nativeSetDragImage }) {
              setCustomNativeDragPreview({
                render({ container }) {
                  setState({ type: "preview", container });
                  return () => setState({ type: "idle" });
                },
                nativeSetDragImage,
              });
            },
          })
        : () => {},

      usage !== "drag"
        ? dropTargetForElements({
            element: cardEl,
            getData: ({ input, element }) => {
              const data = { type: dropType, cardId: id };
              return attachClosestEdge(data, {
                input,
                element,
                allowedEdges: ["top", "bottom"],
              });
            },
            getIsSticky: () => false,
            canDrop: () => type !== undefined && [ObjectType.Folder, ObjectType.Group].includes(type),
            onDragEnter: (args) => {
              if (args.source.data.cardId !== args.self.data.cardId) {
                setIsDragOver(
                  (args.location?.current?.dropTargets &&
                    args.location.current.dropTargets[0] &&
                    args.location.current.dropTargets[0].data?.cardId &&
                    (args.location.current.dropTargets[0].data.cardId as number) === id) as boolean
                );
              }
            },
            onDropTargetChange: (args) => {
              setIsDragOver(
                (args.location?.current?.dropTargets &&
                  args.location.current.dropTargets[0] &&
                  args.location.current.dropTargets[0].data?.cardId &&
                  (args.location.current.dropTargets[0].data.cardId as number) === id) as boolean
              );
            },
            onDragLeave: (args) => {
              setIsDragOver(false);
            },
            onDrop: (args) => {
              setIsDragOver(false);
              let src: number = args.source.data.cardId as number;
              let dst = args.location.current.dropTargets[0].data.cardId as number as number;
              if (src === dst || dst !== id) return;

              const sources: number[] = idsRef.current ? [...idsRef.current.filter((x) => x !== src), src] : [src];

              assetService
                .MoveObjectAsync({
                  targetParentObjectId: dst,
                  objectIds: sources,
                })
                .then((response) => {
                  if (response.success) {
                    if (response.objectId === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
                      enqueueCustomSnackbar(response.message!, "primary");
                    enqueueCustomSnackbar(t("alert:success.movedAssetsToParent", { id: dst }), "success");
                    dispatch(MoveChildrenInTreeObj({ children: sources, parentObjectId: dst, context }));
                    dispatch(RemoveObjs(sources));
                  } else
                    enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
                });
            },
          })
        : () => {}
    );
  }, [id, dropType, type, withPortal]);

  return { isDragging, isDragOver, state, ref };
};

export default useDraggable;
